"use client";

import React, { useState } from "react";
import { sendEvent, EventNames } from "@/lib/utils/analytics";
import { type TouchpointType } from "@/lib/quiz-attribution";
import { QUIZ_SOURCE } from "@/components/quiz/constants";

const faqData = [
  {
    question: "What results does Inito give?",
    answer:
      "Inito gives results as: High Fertility, Peak Fertility or Ovulation Confirmed based on your own baseline hormone values. Inito also shows you the charts and actual urinart values of all 4 hormones - Estrogren, LH, PdG (urine metabolite of progesterone) and FSH.",
  },
  {
    question: "Do I need to use a test strip for each hormone?",
    answer:
      "No, you do not need separate strips. Inito is the only fertility monitor that measures Estrogen, LH, PdG (urine metabolite of progesterone), and FSH on a single test strip. Each test strip is one-time use.",
  },
  {
    question: "How many test strips are required per cycle?",
    answer:
      "The number of tests depends on the irregularity and length of your cycles. For someone with an average cycle length of 30 days, 12-15 tests may be required to predict the fertile window and confirm ovulation. The Inito app will assign tests to you only on the required days based on cycle length and hormone levels.",
  },
  {
    question:
      "Where can I buy more refill strips? Do I need to buy a new monitor every cycle?",
    answer:
      "Additional boxes of strips are available in the Shop page in the Inito app. You don't need to purchase the monitor again, that is a one-time purchase.",
  },
  {
    question: "Are there any limits to specific hormone thresholds?",
    answer:
      "Inito is designed to read all kinds of drops and surges in hormone levels. However, the set limits of measurement for E3G is 600 ng/ml, PdG is 40 ug/ml and LH & FSH are 40 mIU/ml.",
  },
  {
    question: "Will Inito work if I have PCOS?",
    answer:
      "Inito shows you actual values of your hormones, which can help you identify your fertile days and confirm ovulation regardless of PCOS.",
  },
  {
    question: "Do medications affect results?",
    answer:
      "Inito will be able to measure the actual values of hormones which have been metabolised in your urine, but it cannot differentiate between the hormones naturally present in your body and the any supplemented hormones.",
  },
  {
    question: "Can I use HSA benefits to purchase Inito?",
    answer:
      "Yes, you can use HSA or FSA benefits for the Inito starter kit as well as strips.",
  },
  {
    question: "What is Inito's regulatory status in the US?",
    answer:
      "The Inito Fertility Monitor is registered with the U.S. Food and Drug Administration (FDA) as a Class I medical device that is exempt from FDA's premarket notification submission requirements. The registration status should not be misunderstood as suggesting that the Inito Fertility Monitor has been reviewed, cleared or approved by FDA.",
  },
];

const FAQ: React.FC<{ touchpointType?: TouchpointType }> = ({ touchpointType }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    const isOpening = openIndex !== index;
    setOpenIndex(isOpening ? index : null);
    if (isOpening) {
      const question = faqData[index]?.question ?? "";
      sendEvent(EventNames.FAQ_DROP_DOWN_CLICKED, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
        question_type: question,
        section_type: "faq_section",
      });
    }
  };

  return (
    <div className="flex flex-col justify-center items-start px-5 pb-12 mb-[50px] bg-[#f3f4ed]">
      <div className="pb-5">
        <h2 className="title-32 max-w-[330px] mb-2.5 mt-12 text-[#232221] md:max-w-[450px]">
          Your top questions, answered
        </h2>
      </div>
      <div className="flex flex-col items-start gap-1">
        {faqData.map((item, index) => (
          <div
            key={index}
            className={`w-full ${
              index === faqData.length - 1
                ? "border-none pb-5"
                : "border-b border-[#e4e4e4]"
            }`}
          >
            <div
              className="flex justify-between items-center py-5 cursor-pointer w-full"
              onClick={() => handleToggle(index)}
            >
              <p className="title-18 text-[#232221] pr-5">
                {item.question}
              </p>
              <span className="title-20 font-bold cursor-pointer flex items-center justify-end w-10">
                <img
                  src="https://cdn.inito.com/rebrand_quiz/v1/others/downArrow.svg"
                  alt="toggle icon"
                  className={`shrink-0 transition-transform duration-300 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </span>
            </div>
            {openIndex === index && (
              <p className="title-14 md:title-16 text-[#474646] pb-4">
                {item.answer}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQ;
